package com.veldekar.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.toAttrs
import com.veldekar.HeadlineTextStyle
import com.veldekar.charts.ChartResources
import com.veldekar.components.layouts.PageLayoutData
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text

@InitRoute
fun initSessionHeatmapPage(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Session Heatmap",
            ogTitle = "Veldekar Session Heatmap",
            ogDescription = "Grid displaying the density of D&D sessions played across years and seasons.",
            ogImage = "session-heatmap-preview.png",
            ogImageWidth = 1200,
            ogImageHeight = 630,
            ogImageAlt = "Heatmap showing session density"
        )
    )
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun SessionHeatmapPage() {
    val chartDataList = ChartResources.sessionsByYear()

    Column(Modifier.fillMaxWidth().padding(2.cssRem).gap(4.cssRem)) {
        Column(Modifier.fillMaxWidth().gap(1.cssRem)) {
            H2(HeadlineTextStyle.toAttrs()) {
                Text("Sessions per Season Heatmap")
            }
            HeatmapGrid(chartDataList)
        }
    }
}

@Composable
fun HeatmapGrid(data: List<com.veldekar.charts.SessionYearData>) {
    val maxCount = data.maxOfOrNull { maxOf(it.winter, it.spring, it.summer, it.fall) } ?: 1

    // Simple CSS Grid for Heatmap
    Div(attrs = {
        style {
            property("display", "grid")
            property("grid-template-columns", "auto repeat(4, 1fr)")
            property("gap", "4px")
            property("max-width", "800px")
        }
    }) {
        // Header
        Div(attrs = { style { property("font-weight", "bold"); property("padding", "8px") } }) { Text("Year") }
        Div(attrs = { style { property("font-weight", "bold"); property("padding", "8px"); property("text-align", "center") } }) { Text("Winter") }
        Div(attrs = { style { property("font-weight", "bold"); property("padding", "8px"); property("text-align", "center") } }) { Text("Spring") }
        Div(attrs = { style { property("font-weight", "bold"); property("padding", "8px"); property("text-align", "center") } }) { Text("Summer") }
        Div(attrs = { style { property("font-weight", "bold"); property("padding", "8px"); property("text-align", "center") } }) { Text("Fall") }

        // Data Rows
        data.forEach { yearData ->
            Div(attrs = {
                style {
                    property("font-weight", "bold");
                    property("padding", "8px");
                    property("display", "flex");
                    property("align-items", "center")
                }
            }) { Text("${yearData.year}") }
            HeatmapCell(yearData.winter, maxCount)
            HeatmapCell(yearData.spring, maxCount)
            HeatmapCell(yearData.summer, maxCount)
            HeatmapCell(yearData.fall, maxCount)
        }
    }
}

@Composable
fun HeatmapCell(count: Int, maxCount: Int) {
    // Calculate intensity 0.1 to 1.0 based on count relative to maxCount
    val intensity = if (count == 0) 0.05 else 0.2 + (0.8 * (count.toDouble() / maxCount.toDouble()))

    Div(attrs = {
        style {
            property("background-color", "rgba(59, 130, 246, $intensity)")
            property("padding", "8px")
            property("text-align", "center")
            property("border-radius", "4px")
            property("min-height", "40px")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            if (intensity > 0.5) property("color", "white") else property("color", "inherit")
        }
    }) {
        Text(if (count > 0) count.toString() else "-")
    }
}
